import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useCart } from "../context/CartContext";
import { useAuth } from "../AuthContext";
import { createCartPayPalOrder, captureCartPayPalOrder } from "../services/paypalService";
import { PAYPAL_CONFIG, validatePayPalConfig } from "../config/paypalConfig";
import "../css/Cart.css";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? 0).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

function suggestionPriceLabel(s) {
  if (s.category === "suite" || s.category === "villa") return `${fmt(s.priceNumeric)}/night`;
  if (s.category === "pavilion") return `${fmt(s.priceNumeric)}/person`;
  return fmt(s.priceNumeric);
}

function itemLineTotal(item) {
  const p = item.priceNumeric || 0;
  if (item.category === "suite" || item.category === "villa") return p * (item.nights || 1);
  if (item.category === "pavilion") return p * (item.guests || 1);
  return p;
}

function itemPriceDetail(item) {
  const p = item.priceNumeric || 0;
  if (item.category === "suite" || item.category === "villa")
    return `${fmt(p)}/night × ${item.nights || 1} night${(item.nights || 1) === 1 ? "" : "s"}`;
  if (item.category === "pavilion")
    return `${fmt(p)}/person × ${item.guests || 1} guest${(item.guests || 1) === 1 ? "" : "s"}`;
  return "per session";
}

function bookingPath(category) {
  if (category === "wellness") return "/reserve-wellness";
  if (category === "pavilion") return "/reserve-pavilion";
  return "/booking";
}

function badgeLabel(category) {
  return { suite: "Suite", villa: "Villa", wellness: "Wellness", pavilion: "PB Pavilion" }[category] || category;
}

// ── video background (shared by multiple steps) ───────────────────────────────

function VideoBg() {
  return (
    <>
      <video
        autoPlay muted loop playsInline
        style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                 objectFit: "cover", zIndex: -2 }}
      >
        <source src="/videos/sunsetbluedark.mp4" type="video/mp4" />
      </video>
      <div style={{ position: "fixed", inset: 0, zIndex: -1,
                    background: "linear-gradient(to bottom, rgba(4,8,25,0.78) 0%, rgba(8,14,40,0.7) 100%)" }} />
    </>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function Cart() {
  const { items, removeItem, clearCart, getSubtotal } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // "cart" → reviewing items  |  "payment" → PayPal buttons showing
  const [checkoutStep, setCheckoutStep] = useState("cart");
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const paymentStarted = useRef(false);

  const [suggestions, setSuggestions] = useState([]);

  // Fetch complementary suggestions whenever cart contents change (only on "cart" step).
  // Pulls available items from missing categories; excludes items already in the cart.
  useEffect(() => {
    if (items.length === 0 || checkoutStep !== "cart") { setSuggestions([]); return; }

    const hasAccommodation = items.some((i) => i.category === "suite" || i.category === "villa");
    const hasWellness      = items.some((i) => i.category === "wellness");
    const hasPavilion      = items.some((i) => i.category === "pavilion");

    if (hasAccommodation && hasWellness && hasPavilion) { setSuggestions([]); return; }

    const cartItemIds = new Set(items.map((i) => i.itemId));

    const fetches = [];

    if (!hasAccommodation) {
      fetches.push(
        getDocs(collection(db, "villas")).then((snap) =>
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((d) => d.available !== false && !cartItemIds.has(d.id))
            .slice(0, 3)
            .map((d) => ({
              id: d.id,
              category: String(d.name || "").toLowerCase().endsWith("suite") ? "suite" : "villa",
              name: d.name,
              price: d.price,
              priceNumeric: parsePrice(d.price),
              description: d.description || null,
            }))
        )
      );
    }

    if (!hasWellness) {
      fetches.push(
        getDocs(collection(db, "wellnessServices")).then((snap) =>
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((d) => d.available !== false && !cartItemIds.has(d.id))
            .slice(0, 3)
            .map((d) => ({
              id: d.id,
              category: "wellness",
              name: d.name,
              price: d.price,
              priceNumeric: parsePrice(d.price),
              description: d.description || null,
            }))
        )
      );
    }

    if (!hasPavilion) {
      fetches.push(
        getDocs(collection(db, "pavilionEvents")).then((snap) =>
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((d) => d.available !== false && !cartItemIds.has(d.id))
            .slice(0, 3)
            .map((d) => ({
              id: d.id,
              category: "pavilion",
              name: d.title || d.name,
              price: d.price,
              priceNumeric: parsePrice(d.price),
              description: d.description || null,
            }))
        )
      );
    }

    Promise.all(fetches)
      .then((results) => setSuggestions(results.flat()))
      .catch(() => setSuggestions([]));
  }, [items, checkoutStep]);

  // Navigate to the relevant booking form with the suggestion pre-selected so
  // the user can fill in dates and add the item to their cart from there.
  const handleSuggestionAdd = (s) => {
    navigate(bookingPath(s.category), {
      state: {
        cartItem: {
          itemId: s.id,
          itemName: s.name,
          category: s.category,
          price: s.price,
          priceNumeric: s.priceNumeric,
        },
      },
    });
  };

  // ── items to pass to PayPal — only fields needed for price calculation ───────
  const orderItems = items.map((i) => ({
    category: i.category,
    itemId: i.itemId,
    checkIn: i.checkIn || null,
    checkOut: i.checkOut || null,
    nights: i.nights || null,
    guests: i.guests || null,
  }));

  return (
    <div className="cart-page-wrapper">
      <VideoBg />

      <div className="cart-content">
        <div className="cart-header">
          <h1>Your Cart</h1>
          <p className="cart-subtitle">
            {items.length === 0
              ? "No items saved yet"
              : `${items.length} item${items.length === 1 ? "" : "s"} saved`}
          </p>
        </div>
        <div className="cart-gold-sep" />

        {/* ── Not signed in ── */}
        {!currentUser ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">🔒</div>
            <h2>Sign in to view your cart</h2>
            <p>Your cart persists in this browser. Sign in to start adding reservations.</p>
            <div className="cart-empty-links">
              <Link to="/sign-in" className="cart-empty-link">Sign In</Link>
              <Link to="/sign-up" className="cart-empty-link">Create Account</Link>
            </div>
          </div>

        /* ── Empty cart ── */
        ) : items.length === 0 ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Browse our experiences and add them to your cart before booking.</p>
            <div className="cart-empty-links">
              <Link to="/booking" className="cart-empty-link">Suites &amp; Villas</Link>
              <Link to="/reserve-wellness" className="cart-empty-link">Wellness Center</Link>
              <Link to="/reserve-pavilion" className="cart-empty-link">PB Pavilion</Link>
            </div>
          </div>

        /* ── Cart with items ── */
        ) : (
          <>
          <div className="cart-grid">

            {/* ── Left: item cards ── */}
            <div className="cart-items-list">
              {items.map((item) => (
                <div key={item.cartItemId} className="cart-item-card">
                  <div className="cart-item-top">
                    <span className={`cart-category-badge ${item.category}`}>
                      {badgeLabel(item.category)}
                    </span>
                    {checkoutStep === "cart" && (
                      <button
                        className="cart-remove-btn"
                        onClick={() => removeItem(item.cartItemId)}
                        title="Remove from cart"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <p className="cart-item-name">{item.itemName}</p>

                  {item.checkIn && item.checkOut ? (
                    <p className="cart-item-meta">
                      {item.checkIn} &rarr; {item.checkOut}
                      {item.nights ? ` · ${item.nights} night${item.nights === 1 ? "" : "s"}` : ""}
                    </p>
                  ) : item.date ? (
                    <p className="cart-item-meta">Date: {item.date}</p>
                  ) : null}

                  {item.guests ? (
                    <p className="cart-item-meta">
                      {item.guests} guest{item.guests === 1 ? "" : "s"}
                    </p>
                  ) : null}

                  {item.primaryGuest && (
                    <p className="cart-item-meta">Guest: {item.primaryGuest}</p>
                  )}

                  <div className="cart-item-bottom">
                    <div>
                      <span className="cart-item-price">{fmt(itemLineTotal(item))}</span>
                      <span className="cart-item-price-detail">
                        est. — {itemPriceDetail(item)}
                      </span>
                    </div>
                    {/* "Book This Item" pre-fills the reservation form */}
                    {checkoutStep === "cart" && (
                      <button
                        className="cart-book-btn"
                        onClick={() =>
                          navigate(bookingPath(item.category), { state: { cartItem: item } })
                        }
                      >
                        Book This Item
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Right: summary + checkout ── */}
            <div className="cart-summary-panel">
              <h3>Order Summary</h3>

              {items.map((item) => (
                <div key={item.cartItemId} className="cart-summary-row">
                  <span style={{ maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.itemName}
                  </span>
                  <span>{fmt(itemLineTotal(item))}</span>
                </div>
              ))}

              <div className="cart-summary-total">
                <span>Estimated Total</span>
                <span>{fmt(getSubtotal())}</span>
              </div>

              <p className="cart-summary-disclaimer">
                Final amounts are confirmed server-side at checkout and may differ slightly
                from estimates above.
              </p>

              {/* ── "cart" step: action buttons ── */}
              {checkoutStep === "cart" && (
                <>
                  <button
                    className="cart-checkout-btn"
                    onClick={() => { setCheckoutError(null); setCheckoutStep("payment"); }}
                  >
                    Proceed to Checkout
                  </button>
                  <button className="cart-clear-btn" onClick={clearCart}>
                    Clear All Items
                  </button>
                  <Link to="/" className="cart-browse-btn">
                    Continue Browsing
                  </Link>
                </>
              )}

              {/* ── "payment" step: PayPal buttons ── */}
              {checkoutStep === "payment" && (
                <div className="cart-payment-section">
                  <p className="cart-payment-label">Complete your payment securely:</p>

                  {checkoutError && (
                    <div className="cart-checkout-error">{checkoutError}</div>
                  )}

                  {!validatePayPalConfig() ? (
                    <p style={{ color: "#ffa1a1", fontSize: "0.85rem" }}>
                      PayPal is not configured. Add REACT_APP_PAYPAL_CLIENT_ID to .env.
                    </p>
                  ) : (
                    <PayPalScriptProvider options={{ clientId: PAYPAL_CONFIG.clientId, currency: "USD" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", shape: "rect" }}
                        disabled={isProcessing}
                        createOrder={async () => {
                          paymentStarted.current = true;
                          setCheckoutError(null);
                          try {
                            const result = await createCartPayPalOrder({ items: orderItems });
                            return result.orderId;
                          } catch (err) {
                            setCheckoutError(err.message || "Could not start checkout. Please try again.");
                            throw err;
                          }
                        }}
                        onApprove={async (data) => {
                          setIsProcessing(true);
                          setCheckoutError(null);
                          try {
                            const result = await captureCartPayPalOrder({ orderId: data.orderID, items });
                            clearCart();
                            navigate("/booking-confirmed", {
                              state: {
                                pointsAwarded: result.pointsAwarded,
                                totalCharged: result.totalCharged,
                                bookingCount: result.bookingIds?.length || items.length,
                                newBalance: result.newBalance ?? null,
                              },
                            });
                          } catch (err) {
                            setCheckoutError(
                              err.message ||
                              "Payment was captured but something went wrong saving your booking. Please contact us."
                            );
                          } finally {
                            setIsProcessing(false);
                          }
                        }}
                        onCancel={() => {
                          if (paymentStarted.current) {
                            setCheckoutError("Payment was cancelled. Your cart is still saved.");
                            paymentStarted.current = false;
                          }
                          setCheckoutStep("cart");
                        }}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          setCheckoutError("Something went wrong with PayPal. Please try again.");
                        }}
                      />
                    </PayPalScriptProvider>
                  )}

                  <button
                    className="cart-back-btn"
                    onClick={() => {
                      paymentStarted.current = false;
                      setCheckoutError(null);
                      setCheckoutStep("cart");
                    }}
                    disabled={isProcessing}
                  >
                    ← Back to Cart
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* ── You may also like ── */}
          {checkoutStep === "cart" && suggestions.length > 0 && (
            <div className="cart-suggestions">
              <div className="cart-suggestions-heading">
                <span className="cart-suggestions-spark">✦</span>
                <h2>You May Also Like</h2>
                <div className="cart-suggestions-sep" />
              </div>
              <div className="cart-suggestions-grid">
                {suggestions.map((s) => (
                  <div key={`${s.category}-${s.id}`} className="cart-suggestion-card">
                    <div className="cart-item-top" style={{ marginBottom: 10 }}>
                      <span className={`cart-category-badge ${s.category}`}>
                        {badgeLabel(s.category)}
                      </span>
                    </div>
                    <p className="cart-suggestion-name">{s.name}</p>
                    {s.description && (
                      <p className="cart-suggestion-desc">{s.description}</p>
                    )}
                    <div className="cart-suggestion-footer">
                      <span className="cart-suggestion-price">
                        {suggestionPriceLabel(s)}
                      </span>
                      <button
                        className="cart-suggestion-btn"
                        onClick={() => handleSuggestionAdd(s)}
                      >
                        Add to Cart →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
